import {
  Building2Icon,
  CalendarRangeIcon,
  MailPlusIcon,
  NetworkIcon,
  UserCogIcon,
  UserRoundIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDepartments } from '@/shared/api/directory';
import { holdsPermission, PERMISSIONS, useCurrentUserOrNull } from '@/shared/auth';
import type { CommandItem, CommandSource } from '@/shared/registry/types';

const normalize = (value: string) => value.trim().toLocaleLowerCase().replaceAll('ё', 'е');

/** Company pages and departments in the ⌘K palette (people are listed by the shell from the directory). */
function useCompanyCommands(query: string): { items: CommandItem[]; isLoading?: boolean } {
  const { t } = useTranslation('company');
  const me = useCurrentUserOrNull();
  const q = normalize(query);
  const departments = useDepartments({ enabled: q.length >= 2 });
  return useMemo(() => {
    const pages: (CommandItem & { any?: readonly string[]; scoped?: boolean })[] = [
      {
        id: 'company:profile',
        label: t('commands.myProfile'),
        icon: UserRoundIcon,
        to: me ? `/company/users/${me.id}` : '/company',
        keywords: ['profile', 'профиль', 'профіль'],
      },
      {
        id: 'company:structure',
        label: t('commands.structure'),
        icon: NetworkIcon,
        to: '/company/structure',
        keywords: ['structure', 'org', 'структура', 'оргструктура'],
      },
      {
        id: 'company:absences',
        label: t('commands.absences'),
        icon: CalendarRangeIcon,
        to: '/company/absences',
        keywords: ['absence', 'vacation', 'отпуск', 'відпустка'],
      },
      {
        id: 'company:users',
        label: t('commands.users'),
        icon: UserCogIcon,
        to: '/company/admin/users',
        any: [PERMISSIONS.identity.usersManage],
        scoped: true,
        keywords: ['users', 'admin'],
      },
      {
        id: 'company:invite',
        label: t('commands.invite'),
        icon: MailPlusIcon,
        to: '/company/admin/invitations',
        any: [PERMISSIONS.identity.invitationsManage],
        keywords: ['invite', 'пригласить', 'запросити'],
      },
    ];
    const visiblePages = pages
      .filter((page) => !page.any || holdsPermission(me, page.any, { scoped: page.scoped }))
      .filter(
        (page) =>
          !q || normalize(page.label).includes(q) || page.keywords?.some((k) => normalize(k).includes(q)),
      )
      .map(({ any: _any, scoped: _scoped, ...page }) => page);
    if (!q) return { items: [] };
    const matches =
      q.length >= 2
        ? (departments.data ?? [])
            .filter((d) => normalize(d.name).includes(q))
            .slice(0, 5)
            .map<CommandItem>((d) => ({
              id: `department:${d.id}`,
              label: d.name,
              description: d.head
                ? t('commands.departmentHead', { name: d.head.fullName })
                : t('search.department'),
              icon: Building2Icon,
              to: `/company/structure?panel=department:${d.id}`,
            }))
        : [];
    return { items: [...matches, ...visiblePages], isLoading: q.length >= 2 && departments.isLoading };
  }, [t, me, q, departments.data, departments.isLoading]);
}

export const companyCommandSources: CommandSource[] = [
  {
    id: 'company',
    headingKey: 'company:commands.heading',
    order: 70,
    minQueryLength: 1,
    useItems: useCompanyCommands,
  },
];
